// Minimal diagnostics banner for oEmbed 424 errors
import React from 'react';
import type { OEmbedRequiredError } from '../types/api';

interface DiagnosticsBannerProps {
	error: OEmbedRequiredError | null;
	onClose?: () => void;
}

function humanMessage(e: OEmbedRequiredError): string {
	const plat = e.platform;
	switch (e.reason) {
		case 'E_YT_OEMBED_JSON':
			return 'YouTube oEmbed failed. The video may be private/age-restricted, or the URL is invalid.';
		case 'E_PROXY_OEMBED_HTTP':
			return `${plat} oEmbed call failed. The post may be private, age-restricted, or the domain is not approved for Meta.`;
		case 'invalid_oembed_payload':
			return 'The provider did not return valid oEmbed data. The post may be unavailable or blocked.';
		case 'oembed_fetch_failed':
			return 'Network error fetching oEmbed. Please try again, or check your connection.';
		default:
			return 'oEmbed is required to proceed, but the provider failed to respond correctly.';
	}
}

export const DiagnosticsBanner: React.FC<DiagnosticsBannerProps> = ({ error, onClose }) => {
	if (!error) return null;
	const tips: string[] = [
		'If Instagram/Facebook: ensure the domain is approved in your Meta app and the content is public.',
		'If the post is private or age-gated, oEmbed will not work.',
		'Expired tokens or missing domain parameter can also cause Meta oEmbed to fail.',
	];
	return (
		<div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800">
			<div className="flex items-start justify-between gap-2">
				<div>
					<p className="font-semibold">oEmbed required</p>
					<p className="text-sm">{humanMessage(error)}</p>
					<div className="mt-2 text-xs text-red-700">
						<p>
							Platform: <span className="font-mono">{error.platform}</span> · Status: <span className="font-mono">{error.debug?.status || 'unknown'}</span>
						</p>
						{error.debug?.body && (
							<details className="mt-1">
								<summary className="cursor-pointer">Debug body (truncated)</summary>
								<pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-white/60 p-2 text-[10px] leading-snug">
									{error.debug.body}
								</pre>
							</details>
						)}
					</div>
					<ul className="mt-2 list-disc pl-5 text-xs">
						{tips.map((t) => (
							<li key={t}>{t}</li>
						))}
					</ul>
				</div>
				{onClose && (
					<button onClick={onClose} className="rounded bg-red-100 px-2 py-1 text-xs hover:bg-red-200">Dismiss</button>
				)}
			</div>
		</div>
	);
};

export default DiagnosticsBanner;


